"use client";

import { needsPortalCustomHostLookup } from "@/lib/host-audience";
import { api } from "@v1/backend/convex/_generated/api";
import {
  foreignOrgPublicPageOrigin,
  orgSlugOfHost,
} from "@v1/backend/logic/organizations/public_org_host";
import { useQuery } from "convex/react";
import { useEffect, useState } from "react";

/** Org of the current host: label hosts from the name, portal custom domains via lookup. */
function useHostOrgSlug(): string | null | undefined {
  const [hostname, setHostname] = useState<string | null>(null);
  useEffect(() => {
    setHostname(window.location.hostname);
  }, []);
  const labelSlug = hostname === null ? null : orgSlugOfHost(hostname);
  const lookup =
    hostname !== null &&
    labelSlug === null &&
    needsPortalCustomHostLookup(hostname);
  const customSlug = useQuery(
    api.portal.branding.getOrgSlugForPortalCustomHost,
    lookup && hostname !== null ? { hostname } : "skip",
  );
  if (hostname === null) return undefined;
  return lookup ? customSlug : labelSlug;
}

/** Phishing guard: foreign org's page moves to its own host. True once this host may render it; null = owner unknown. */
export function usePublicOrgHostGate(orgSlug: string | null): boolean {
  const hostOrgSlug = useHostOrgSlug();
  const target =
    orgSlug !== null && typeof hostOrgSlug === "string"
      ? foreignOrgPublicPageOrigin(hostOrgSlug, orgSlug)
      : null;
  useEffect(() => {
    if (target === null) return;
    const { pathname, search, hash } = window.location;
    window.location.replace(`${target}${pathname}${search}${hash}`);
  }, [target]);
  if (orgSlug === null) return true;
  return hostOrgSlug !== undefined && target === null;
}

/** Non-blocking variant for server-rendered pages; middleware already covers label hosts. */
export function PublicOrgHostGuard({
  orgSlug,
}: {
  readonly orgSlug: string | null;
}): null {
  usePublicOrgHostGate(orgSlug);
  return null;
}
